// ** React Imports
import { lazy } from "react";
import { Navigate } from "react-router";

const AdminUsersList = lazy(() => import("../../views/apps/user/admin-users"));
const AdminUserView = lazy(() => import("../../views/apps/user/admin-users/view"));
const ClientUsersList = lazy(() => import("../../views/apps/user/client-users"));
const ClientUserView = lazy(() => import("../../views/apps/user/client-users/view"));
const BusinessUsersList = lazy(() => import("../../views/apps/user/business-users"));
const BusinessUserView = lazy(() => import("../../views/apps/user/business-users/view"));

const BookingList = lazy(() => import("../../views/apps/operations/booking/list"));
const BookingView = lazy(() => import("../../views/apps/operations/booking/view"));

const ProductList = lazy(() => import("../../views/apps/operations/product/list"));
const ProductView = lazy(() => import("../../views/apps/operations/product/view"));
const ServiceList = lazy(() => import("../../views/apps/operations/service/list"));
const ServiceView = lazy(() => import("../../views/apps/operations/service/view"));
const BusinessList = lazy(() => import("../../views/apps/business/businesses/list"));
const BusinessView = lazy(() => import("../../views/apps/business/businesses/view"));
const BusinessPolicyList = lazy(() => import("../../views/apps/business/policies/list"));
const DeeplinkList = lazy(() => import("../../views/apps/operations/deeplink/list"));
const DeeplinkView = lazy(() => import("../../views/apps/operations/deeplink/view"));
const HelpdeskList = lazy(() => import("../../views/apps/support/helpdesk/list"));
const HelpdeskView = lazy(() => import("../../views/apps/support/helpdesk/view"));
const FeedbackList = lazy(() => import("../../views/apps/support/feedback/list"));
const FeedbackView = lazy(() => import("../../views/apps/support/feedback/view"));
const DynamicFormList = lazy(
  () => import("../../views/apps/form-workflow/forms/list")
);
const DynamicFormView = lazy(
  () => import("../../views/apps/form-workflow/forms/view")
);
const DynamicFormBuilder = lazy(
  () => import("../../views/apps/form-workflow/forms/builder")
);
const NotificationList = lazy(
  () => import("../../views/apps/platform-configuration/notification-templates")
);
const NotificationAddEdit = lazy(
  () => import("../../views/apps/platform-configuration/notification-templates/add-edit")
);
const NotificationView = lazy(
  () => import("../../views/apps/platform-configuration/notification-templates/view")
);
const AppNotificationList = lazy(
  () => import("../../views/apps/platform-configuration/app-notifications")
);
const AppNotificationAddEdit = lazy(
  () => import("../../views/apps/platform-configuration/app-notifications/add-edit")
);
const AppNotificationView = lazy(
  () => import("../../views/apps/platform-configuration/app-notifications/view")
);
const AppNotificationLogsList = lazy(
  () => import("../../views/apps/platform-configuration/notification-logs")
);
const AppNotificationLogView = lazy(
  () => import("../../views/apps/platform-configuration/notification-logs/view")
);
const TransactionList = lazy(
  () => import("../../views/apps/financial/transactions/list")
);
const TransactionView = lazy(
  () => import("../../views/apps/financial/transactions/view")
);
const FinancialRefundList = lazy(
  () => import("../../views/apps/financial/refunds/list")
);
const FinancialRefundView = lazy(
  () => import("../../views/apps/financial/refunds/view")
);
const ReturnList = lazy(
  () => import("../../views/apps/financial/returns/list")
);
const ReturnView = lazy(
  () => import("../../views/apps/financial/returns/view")
);
const FeeConfiguration = lazy(
  () => import("../../views/apps/financial/fees/list")
);
const AnalyticsDashboard = lazy(
  () => import("../../views/apps/analytics-reporting/dashboard")
);
const CancellationPoliciesPage = lazy(
  () => import("../../views/apps/financial/cancellation-policies/list")
);
const InvoiceList = lazy(
  () => import("../../views/apps/financial/invoices/list")
);
const InvoiceView = lazy(
  () => import("../../views/apps/financial/invoices/view")
);
const PayoutList = lazy(() => import("../../views/apps/financial/payouts/list"));
const PayoutView = lazy(
  () => import("../../views/apps/financial/payouts/view")
);
const EducationalContent = lazy(() => import("../../views/apps/content/educational"));
const EducationalGuideView = lazy(
  () => import("../../views/apps/content/educational/view")
);
const FeaturedContent = lazy(() => import("../../views/apps/content/featured"));
const FeaturedContentView = lazy(
  () => import("../../views/apps/content/featured/view")
);
const LegalPages = lazy(() => import("../../views/apps/content/legal"));
const LegalPageAddEdit = lazy(
  () => import("../../views/apps/content/legal/add-edit")
);
const FAQContent = lazy(() => import("../../views/apps/content/faq"));
const FeatureToggles = lazy(() => import("../../views/apps/system/feature-toggles"));
const FeatureToggleView = lazy(
  () => import("../../views/apps/system/feature-toggles/view")
);
const ErrorLogs = lazy(() => import("../../views/apps/system/error-logs"));
const ErrorLogView = lazy(() => import("../../views/apps/system/error-logs/view"));

const ContentModerationReviewQueue = lazy(
  () => import("../../views/apps/content-moderation/review-queue")
);
const ContentModerationReviewView = lazy(
  () => import("../../views/apps/content-moderation/review-queue/view")
);
const ContentModerationReported = lazy(
  () => import("../../views/apps/content-moderation/reported")
);
const ContentModerationReportedView = lazy(
  () => import("../../views/apps/content-moderation/reported/view")
);

const AnalyticsReportList = lazy(
  () => import("../../views/apps/analytics-reporting/reports/list")
);
const AnalyticsReportView = lazy(
  () => import("../../views/apps/analytics-reporting/reports/view")
);
const ScheduledReportList = lazy(
  () => import("../../views/apps/analytics-reporting/scheduled/list")
);
const ScheduledReportView = lazy(
  () => import("../../views/apps/analytics-reporting/scheduled/view")
);
const DataExportList = lazy(
  () => import("../../views/apps/analytics-reporting/exports/list")
);
const DataExportView = lazy(
  () => import("../../views/apps/analytics-reporting/exports/view")
);

const TicketList = lazy(() => import("../../views/apps/support/tickets/list"));
const TicketView = lazy(() => import("../../views/apps/support/tickets/view"));

const Roles = lazy(() => import("../../views/apps/roles-permissions/roles"));
const Permissions = lazy(
  () => import("../../views/apps/roles-permissions/permissions")
);

const AppRoutes = [
  {
    element: <AdminUsersList />,    path: "/apps/user/admin-users",
  },
  {
    element: <AdminUserView />,
    path: "/apps/user/admin-users/view/:id",
  },
  {
    element: <ClientUsersList />,
    path: "/apps/user/client-users",
  },
  {
    element: <ClientUserView />,
    path: "/apps/user/client-users/view/:id",
  },
  {
    element: <BusinessUsersList />,
    path: "/apps/user/business-users",
  },
  {
    element: <BusinessUserView />,
    path: "/apps/user/business-users/view/:id",
  },
  {
    element: <BookingList />,
    path: "/apps/operations/booking/list",
  },
  {
    element: <BookingView />,
    path: "/apps/operations/booking/view/:id",
  },
  {
    element: <ProductList />,
    path: "/apps/operations/product/list",
  },
  {
    element: <ProductView />,
    path: "/apps/operations/product/view/:id",
  },
  {
    element: <ServiceList />,
    path: "/apps/operations/service/list",
  },
  {
    element: <ServiceView />,
    path: "/apps/operations/service/view/:id",
  },
  {
    element: <BusinessList />,
    path: "/apps/business/businesses/list",
  },
  {
    element: <BusinessView />,
    path: "/apps/business/businesses/view/:id",
  },
  {
    element: <BusinessPolicyList />,
    path: "/apps/business/policies/list",
  },
  {
    path: "/apps/business",
    element: <Navigate to="/apps/business/businesses/list" replace />,
  },
  {
    element: <DeeplinkList />,
    path: "/apps/operations/deeplink/list",
  },
  {
    element: <DeeplinkView />,
    path: "/apps/operations/deeplink/view/:id",
  },
  {
    element: <TicketList />,
    path: "/apps/support/tickets/list",
  },
  {
    element: <TicketView />,
    path: "/apps/support/tickets/view/:id",
  },
  {
    element: <Roles />,
    path: "/apps/roles",
  },
  {
    element: <Permissions />,
    path: "/apps/permissions",
  },
  {
    element: <HelpdeskList />,
    path: "/apps/support/helpdesk/list",
  },
  {
    element: <HelpdeskView />,
    path: "/apps/support/helpdesk/view/:id",
  },
  {
    element: <FeedbackList />,
    path: "/apps/support/feedback/list",
  },
  {
    element: <FeedbackView />,
    path: "/apps/support/feedback/view/:id",
  },
  {
    element: <DynamicFormList />,
    path: "/apps/form-workflow/forms",
  },
  {
    element: <DynamicFormBuilder />,
    path: "/apps/form-workflow/forms/builder",
  },
  {
    element: <DynamicFormBuilder />,
    path: "/apps/form-workflow/forms/builder/:id",
  },
  {
    element: <DynamicFormView />,
    path: "/apps/form-workflow/forms/view/:id",
  },
  {
    path: "/apps/platform-configuration",
    element: (
      <Navigate to="/apps/platform-configuration/notification-templates" replace />
    ),
  },
  {
    element: <NotificationList />,
    path: "/apps/platform-configuration/notification-templates",
  },
  {
    element: <NotificationAddEdit />,
    path: "/apps/platform-configuration/notification-templates/add",
  },
  {
    element: <NotificationAddEdit />,
    path: "/apps/platform-configuration/notification-templates/edit/:id",
  },
  {
    element: <NotificationView />,
    path: "/apps/platform-configuration/notification-templates/view/:id",
  },
  {
    element: <AppNotificationList />,
    path: "/apps/platform-configuration/app-notifications",
  },
  {
    element: <AppNotificationAddEdit />,
    path: "/apps/platform-configuration/app-notifications/add",
  },
  {
    element: <AppNotificationAddEdit />,
    path: "/apps/platform-configuration/app-notifications/edit/:id",
  },
  {
    element: <AppNotificationView />,
    path: "/apps/platform-configuration/app-notifications/view/:id",
  },
  {
    element: <AppNotificationLogsList />,
    path: "/apps/platform-configuration/notification-logs",
  },
  {
    element: <AppNotificationLogView />,
    path: "/apps/platform-configuration/notification-logs/view/:id",
  },
  {
    path: "/apps/financial",
    element: <Navigate to="/apps/financial/transactions" replace />,
  },
  {
    path: "/apps/operations",
    element: <Navigate to="/apps/operations/booking/list" replace />,
  },
  {
    path: "/apps/support",
    element: <Navigate to="/apps/support/tickets/list" replace />,
  },
  {
    element: <TransactionList />,
    path: "/apps/financial/transactions",
  },
  {
    element: <TransactionView />,
    path: "/apps/financial/transactions/view/:id",
  },
  {
    element: <FinancialRefundList />,
    path: "/apps/financial/refunds",
  },
  {
    element: <FinancialRefundView />,
    path: "/apps/financial/refunds/view/:id",
  },
  {
    element: <CancellationPoliciesPage />,
    path: "/apps/financial/cancellation-policies",
  },
  {
    element: <ReturnList />,
    path: "/apps/financial/returns",
  },
  {
    element: <ReturnView />,
    path: "/apps/financial/returns/view/:id",
  },
  {
    element: <FeeConfiguration />,
    path: "/apps/financial/fees",
  },
  {
    element: <InvoiceList />,
    path: "/apps/financial/invoices",
  },
  {
    element: <InvoiceView />,
    path: "/apps/financial/invoices/view/:id",
  },
  {
    element: <PayoutList />,
    path: "/apps/financial/payouts",
  },
  {
    element: <PayoutView />,
    path: "/apps/financial/payouts/view/:id",
  },
  {
    element: <EducationalContent />,
    path: "/apps/content/educational",
  },
  {
    element: <EducationalGuideView />,
    path: "/apps/content/educational/view/:id",
  },
  {
    element: <FeaturedContent />,
    path: "/apps/content/featured",
  },
  {
    element: <FeaturedContentView />,
    path: "/apps/content/featured/view/:id",
  },
  {
    path: "/apps/content",
    element: <Navigate to="/apps/content/educational" replace />,
  },
  {
    element: <LegalPages />,
    path: "/apps/content/legal",
  },
  {
    element: <LegalPageAddEdit />,
    path: "/apps/content/legal/add",
  },
  {
    element: <LegalPageAddEdit />,
    path: "/apps/content/legal/edit/:id",
  },
  {
    element: <FAQContent />,
    path: "/apps/content/faq",
  },
  {
    element: <FeatureToggles />,
    path: "/apps/system/feature-toggles",
  },
  {
    element: <FeatureToggleView />,
    path: "/apps/system/feature-toggles/view/:id",
  },
  {
    element: <ErrorLogs />,
    path: "/apps/system/error-logs",
  },
  {
    element: <ErrorLogView />,
    path: "/apps/system/error-logs/view/:id",
  },
  {
    path: "/apps/content-moderation",
    element: <Navigate to="/apps/content-moderation/review-queue" replace />,
  },
  {
    element: <ContentModerationReviewQueue />,
    path: "/apps/content-moderation/review-queue",
  },
  {
    element: <ContentModerationReviewView />,
    path: "/apps/content-moderation/review-queue/view/:id",
  },
  {
    element: <ContentModerationReported />,
    path: "/apps/content-moderation/reported",
  },
  {
    element: <ContentModerationReportedView />,
    path: "/apps/content-moderation/reported/view/:id",
  },
  {
    path: "/apps/analytics-reporting",
    element: <Navigate to="/apps/analytics-reporting/reports" replace />,
  },
  {
    element: <AnalyticsDashboard />,
    path: "/apps/analytics-reporting/dashboard",
  },
  {
    element: <AnalyticsReportList />,
    path: "/apps/analytics-reporting/reports",
  },
  {
    element: <AnalyticsReportView />,
    path: "/apps/analytics-reporting/reports/view/:id",
  },
  {
    element: <ScheduledReportList />,
    path: "/apps/analytics-reporting/scheduled",
  },
  {
    element: <ScheduledReportView />,
    path: "/apps/analytics-reporting/scheduled/view/:id",
  },
  {
    element: <DataExportList />,
    path: "/apps/analytics-reporting/exports",
  },
  {
    element: <DataExportView />,
    path: "/apps/analytics-reporting/exports/view/:id",
  },
];

export default AppRoutes;
